import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAppTheme } from '../App';
import { useAnalytics } from '../hooks/useAnalytics';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Box,
    Alert,
    Stack,
    Link,
    Chip,
} from '@mui/material';
import {
    People as PeopleIcon,
    Description as DescriptionIcon,
    Assessment as AssessmentIcon,
} from '@mui/icons-material';
import ThemeToggle from '../components/ThemeToggle';
import { COLORS, SHADOWS, LIGHT_THEME, DARK_THEME, FOCUS } from '../common/colors';

const Auth: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [isSignUp, setIsSignUp] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { signup, login, currentUser } = useAuth();
    const { isDarkMode, getThemeStyles } = useAppTheme();
    const theme = getThemeStyles();
    const navigate = useNavigate();
    const { analyticsData, loading: analyticsLoading } = useAnalytics();

    // Redirect to home if user is already authenticated
    useEffect(() => {
        if (currentUser) {
            navigate('/', { replace: true });
        }
    }, [currentUser, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (loading) return;

        if (isSignUp) {
            if (!fullName.trim()) {
                setError('Full name is required');
                return;
            }
            if (password !== confirmPassword) {
                setError('Passwords do not match');
                return;
            }
        }

        setLoading(true);
        try {
            if (isSignUp) {
                await signup(email, password, fullName);
            } else {
                await login(email, password);
            }
        } catch (error) {
            setError('Authentication error. Please try again.');
            console.error('Authentication error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100vw',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.background,
                overflow: 'hidden',
                position: 'relative',
                py: 4,
            }}
        >
            {/* Theme Toggle positioned in top right */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 24,
                    right: 24,
                    zIndex: 10,
                }}
            >
                <ThemeToggle />
            </Box>

            {/* Auth mode switcher text above the card */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography
                    variant="body1"
                    sx={{
                        color: theme.textSecondary,
                        fontSize: '0.95rem',
                    }}
                >
                    {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
                    <Link
                        component="button"
                        variant="body1"
                        onClick={() => setIsSignUp(!isSignUp)}
                        sx={{
                            color: isDarkMode ? '#FF8F5A' : '#FE6A2E',
                            fontWeight: 600,
                            textDecoration: 'none',
                            cursor: 'pointer',
                            fontSize: '0.95rem',
                            '&:hover': {
                                textDecoration: 'underline',
                                color: isDarkMode ? '#FF8F5A' : '#FE6A2E',
                            },
                        }}
                    >
                        {isSignUp ? 'Sign in' : 'Create account'}
                    </Link>
                </Typography>
            </Box>

            <Card
                sx={{
                    maxWidth: 620,
                    width: '100%',
                    p: 3,
                    borderRadius: 5,
                    background: theme.cardBackground,
                    boxShadow: `${SHADOWS.card} ${theme.shadowColor}`,
                    backdropFilter: 'blur(16px)',
                    border: `1px solid ${theme.borderColor}`,
                }}
                elevation={0}
            >
                <CardContent sx={{ p: 0 }}>
                    <Typography
                        variant="h4"
                        align="center"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            color: theme.textPrimary,
                            mb: 1,
                            fontSize: { xs: '2rem', sm: '2.5rem' },
                        }}
                    >
                        thevisa.ai
                    </Typography>
                    <Typography
                        variant="h6"
                        align="center"
                        sx={{
                            fontWeight: 400,
                            color: theme.textSecondary,
                            mb: 4,
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                    >
                        The visa you need, advised by the AI you need
                    </Typography>
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={2}>
                            {isSignUp && (
                                <TextField
                                    label="Full Name"
                                    type="text"
                                    value={fullName}
                                    onChange={e => setFullName(e.target.value)}
                                    required
                                    fullWidth
                                    autoComplete="name"
                                    sx={{
                                        borderRadius: 2,
                                        background: theme.inputBackground,
                                        boxShadow: `0 2px 8px 0 ${theme.shadowColor}`,
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                border: `1px solid ${theme.borderColor}`,
                                            },
                                            '&:hover fieldset': {
                                                border: isDarkMode
                                                    ? DARK_THEME.fieldHover
                                                    : LIGHT_THEME.fieldHover,
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: FOCUS.border,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme.textPrimary,
                                            fontWeight: 500,
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.textSecondary,
                                            '&.Mui-focused': {
                                                color: FOCUS.color,
                                            },
                                        },
                                    }}
                                />
                            )}
                            <TextField
                                label="Email"
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                fullWidth
                                autoComplete="email"
                                sx={{
                                    borderRadius: 2,
                                    background: theme.inputBackground,
                                    boxShadow: `0 2px 8px 0 ${theme.shadowColor}`,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: `1px solid ${theme.borderColor}`,
                                        },
                                        '&:hover fieldset': {
                                            border: isDarkMode
                                                ? DARK_THEME.fieldHover
                                                : LIGHT_THEME.fieldHover,
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: FOCUS.border,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme.textPrimary,
                                        fontWeight: 500,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.textSecondary,
                                        '&.Mui-focused': {
                                            color: FOCUS.color,
                                        },
                                    },
                                }}
                            />
                            <TextField
                                label="Password"
                                type="password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                fullWidth
                                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                                sx={{
                                    borderRadius: 2,
                                    background: theme.inputBackground,
                                    boxShadow: `0 2px 8px 0 ${theme.shadowColor}`,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: `1px solid ${theme.borderColor}`,
                                        },
                                        '&:hover fieldset': {
                                            border: isDarkMode
                                                ? DARK_THEME.fieldHover
                                                : LIGHT_THEME.fieldHover,
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: FOCUS.border,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme.textPrimary,
                                        fontWeight: 500,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.textSecondary,
                                        '&.Mui-focused': {
                                            color: FOCUS.color,
                                        },
                                    },
                                }}
                            />
                            {isSignUp && (
                                <TextField
                                    label="Confirm Password"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    required
                                    fullWidth
                                    autoComplete="new-password"
                                    sx={{
                                        borderRadius: 2,
                                        background: theme.inputBackground,
                                        boxShadow: `0 2px 8px 0 ${theme.shadowColor}`,
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                border: `1px solid ${theme.borderColor}`,
                                            },
                                            '&:hover fieldset': {
                                                border: isDarkMode
                                                    ? DARK_THEME.fieldHover
                                                    : LIGHT_THEME.fieldHover,
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: FOCUS.border,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme.textPrimary,
                                            fontWeight: 500,
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.textSecondary,
                                            '&.Mui-focused': {
                                                color: FOCUS.color,
                                            },
                                        },
                                    }}
                                />
                            )}
                            {error && <Alert severity="error">{error}</Alert>}
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                sx={{
                                    mt: 1,
                                    py: 1.5,
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                    borderRadius: 2,
                                    background: theme.chatUserBg,
                                    boxShadow: SHADOWS.primary,
                                    textTransform: 'none',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        backgroundColor: COLORS.primaryHover,
                                    },
                                }}
                            >
                                {loading
                                    ? (isSignUp ? 'Signing Up...' : 'Signing In...')
                                    : (isSignUp ? 'Sign Up' : 'Log In')}
                            </Button>
                        </Stack>
                    </Box>
                </CardContent>
            </Card>

            {/* Analytics Section */}
            <Box sx={{ 
                mt: { xs: 3, sm: 4 }, 
                maxWidth: 620, 
                width: '100%',
                px: { xs: 2, sm: 3 }
            }}>
                <Typography
                    variant="body2"
                    align="center"
                    sx={{
                        color: theme.textSecondary,
                        mb: { xs: 2, sm: 3 },
                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                        fontWeight: 500,
                    }}
                >
                    Trusted by thousands of visa applicants worldwide
                </Typography>
                
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)' },
                    gap: { xs: 1.5, sm: 2 },
                }}>
                    {/* Users Count */}
                    <Card
                        sx={{
                            borderRadius: 2,
                            background: `linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.04) 100%)`,
                            border: `1px solid rgba(76, 175, 80, 0.15)`,
                            boxShadow: `0 4px 12px ${theme.shadowColor}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 6px 16px rgba(76, 175, 80, 0.2)`,
                            },
                        }}
                        elevation={0}
                    >
                        <CardContent sx={{ 
                            p: { xs: 1.5, sm: 2 }, 
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <Box sx={{
                                width: { xs: 32, sm: 40 },
                                height: { xs: 32, sm: 40 },
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                            }}>
                                <PeopleIcon sx={{ 
                                    fontSize: { xs: 18, sm: 22 }, 
                                    color: 'white' 
                                }} />
                            </Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.textPrimary,
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                    lineHeight: 1.2,
                                }}
                            >
                                {analyticsLoading ? '...' : (analyticsData?.numberOfUsers || 0).toLocaleString()}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme.textSecondary,
                                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                    fontWeight: 500,
                                }}
                            >
                                Users Helped
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Documents Generated */}
                    <Card
                        sx={{
                            borderRadius: 2,
                            background: `linear-gradient(135deg, rgba(254, 106, 46, 0.08) 0%, rgba(254, 106, 46, 0.04) 100%)`,
                            border: `1px solid rgba(254, 106, 46, 0.15)`,
                            boxShadow: `0 4px 12px ${theme.shadowColor}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 6px 16px rgba(254, 106, 46, 0.2)`,
                            },
                        }}
                        elevation={0}
                    >
                        <CardContent sx={{ 
                            p: { xs: 1.5, sm: 2 }, 
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <Box sx={{
                                width: { xs: 32, sm: 40 },
                                height: { xs: 32, sm: 40 },
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${COLORS.primary} 0%, #FF8F5A 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 4px 12px rgba(254, 106, 46, 0.3)`,
                            }}>
                                <DescriptionIcon sx={{ 
                                    fontSize: { xs: 18, sm: 22 }, 
                                    color: 'white' 
                                }} />
                            </Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.textPrimary,
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                    lineHeight: 1.2,
                                }}
                            >
                                {analyticsLoading ? '...' : (analyticsData?.personalStatementGenerated || 0).toLocaleString()}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme.textSecondary,
                                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                    fontWeight: 500,
                                }}
                            >
                                Documents Created
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Profiles Analyzed */}
                    <Card
                        sx={{
                            borderRadius: 2,
                            background: `linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.04) 100%)`,
                            border: `1px solid rgba(59, 130, 246, 0.15)`,
                            boxShadow: `0 4px 12px ${theme.shadowColor}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 6px 16px rgba(59, 130, 246, 0.2)`,
                            },
                        }}
                        elevation={0}
                    >
                        <CardContent sx={{ 
                            p: { xs: 1.5, sm: 2 }, 
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <Box sx={{
                                width: { xs: 32, sm: 40 },
                                height: { xs: 32, sm: 40 },
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                            }}>
                                <AssessmentIcon sx={{ 
                                    fontSize: { xs: 18, sm: 22 }, 
                                    color: 'white' 
                                }} />
                            </Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.textPrimary,
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                    lineHeight: 1.2,
                                }}
                            >
                                {analyticsLoading ? '...' : (analyticsData?.profileScored || 0).toLocaleString()}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme.textSecondary,
                                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                    fontWeight: 500,
                                }}
                            >
                                Eligibility Checked
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>

                {/* Trust Indicators */}
                <Box sx={{ 
                    mt: { xs: 2, sm: 3 },
                    display: 'flex',
                    justifyContent: 'center',
                    gap: { xs: 1, sm: 1.5 },
                    flexWrap: 'wrap'
                }}>
                    <Chip
                        label="AI-Powered"
                        size="small"
                        sx={{
                            background: `linear-gradient(135deg, ${COLORS.primary} 0%, #FF8F5A 100%)`,
                            color: 'white',
                            fontWeight: 600,
                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                            height: { xs: 24, sm: 28 },
                        }}
                    />
                    <Chip
                        label="Secure"
                        size="small"
                        sx={{
                            background: `linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)`,
                            color: 'white',
                            fontWeight: 600,
                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                            height: { xs: 24, sm: 28 },
                        }}
                    />
                    <Chip
                        label="Instant"
                        size="small"
                        sx={{
                            background: `linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)`,
                            color: 'white',
                            fontWeight: 600,
                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                            height: { xs: 24, sm: 28 },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default Auth; 